import { queryOptions } from "@tanstack/react-query";
import { orderBy, uniqBy } from "lodash-es";
import {
  createSubscriptionSession,
  useGetBillingQueryOptions,
  useGetNextInvoiceAmountQueryOptions,
  useGetPaymentMethodQueryOptions,
  useGetProductPriceQueryOptions,
  useGetSubscriptionSessionQueryOptions,
  useGetSubscriptionsQueryOptions,
  useGetUpgradePreviewQueryOptions,
} from "~/api/generated/customer/customer";
import {
  type SubscriptionItem,
  type PriceLookupKeyEnum,
  type SubscriptionSessionRequest,
} from "~/api/generated/model";
import { domainQueryKey, domainRootForUrl } from "~/api/orval/query-key";

const CUSTOMER_STALE_TIME = 60_000;

export const customerRootQueryKey = [domainRootForUrl("/customer")] as const;

/**
 * Both endpoints answer 204 No Content when there is nothing to show, which the
 * spec doesn't declare and axios surfaces as an empty string.
 */
const absentOnNoContent = <T>(body: T): T | null =>
  (body as unknown) === "" ? null : body;

/**
 * A manually cancelled and re-created subscription (e.g. for internal test
 * users) leaves several subscriptions for one product; only the most recent
 * one counts.
 */
const selectLatestSubscriptionPerProduct = (
  subscriptions: SubscriptionItem[],
) =>
  uniqBy(
    orderBy(subscriptions, (s) => s.startAt ?? 0, "desc"),
    (s) => s.price.product.productId,
  );

export const customerQueries = {
  subscriptions: () =>
    useGetSubscriptionsQueryOptions({
      query: {
        staleTime: CUSTOMER_STALE_TIME,
        retry: 0,
        select: selectLatestSubscriptionPerProduct,
      },
    }),

  billing: () =>
    useGetBillingQueryOptions({
      query: { staleTime: CUSTOMER_STALE_TIME, retry: 0 },
    }),

  // Creating a Stripe session is a POST, so Orval only generates a mutation for
  // it; the page reads it as a one-shot query that must never be reused.
  checkoutSession: (request: SubscriptionSessionRequest) =>
    queryOptions({
      queryKey: domainQueryKey(
        { data: request },
        { url: "/customer/subscription/session" },
      ),
      staleTime: Infinity,
      gcTime: 0,
      retry: 0,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      queryFn: () => createSubscriptionSession(request),
    }),

  nextInvoiceAmount: () =>
    useGetNextInvoiceAmountQueryOptions({
      query: {
        staleTime: CUSTOMER_STALE_TIME,
        retry: 0,
        select: absentOnNoContent,
      },
    }),

  paymentMethod: () =>
    useGetPaymentMethodQueryOptions({
      query: {
        staleTime: CUSTOMER_STALE_TIME,
        retry: 0,
        select: absentOnNoContent,
      },
    }),

  productPrice: (lookupKey: PriceLookupKeyEnum) =>
    useGetProductPriceQueryOptions(
      { lookupKey },
      { query: { staleTime: 0, retry: 0 } },
    ),

  sessionStatus: (sessionId: string) =>
    useGetSubscriptionSessionQueryOptions(sessionId, {
      query: { staleTime: 0, gcTime: 0, retry: 0 },
    }),

  upgradePreview: (targetPriceLookupKey: PriceLookupKeyEnum) =>
    useGetUpgradePreviewQueryOptions(
      { targetPriceLookupKey },
      { query: { staleTime: 0, retry: 0 } },
    ),
};
